import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Product } from "../types";
import { ClipboardCheck, Loader2, Lock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLocation } from "@/contexts/LocationContext";
import { StockWriteLocationItems } from "@/features/locations/components/StockWriteLocationItems";
import { useOrganization } from "@/contexts/OrganizationContext";
import { trpc } from "@/lib/trpc";
import {
  ClientTargetPicker,
  ClientTargetChip,
} from "./ClientTargetPicker";
import { useConnectedClients } from "../hooks/useConnectedClients";
import { useTargetCatalog } from "../hooks/useTargetCatalog";
import { filterToTargetOrg } from "../utils/filterToTargetOrg";
import { AdjustmentReferenceField } from "./AdjustmentReferenceField";
import { WarningNotice } from "@/components/shared/WarningNotice";
import { isJustInTimePair } from "../utils/stocktakeCsv";

const SHARED_LOCATIONS_ENABLED =
  (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? "") === "true";

interface IndividualStocktakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * BLU-3xx Phase 47.01.2-B-04: optional `targetOrganizationId` routes
   * stocktake corrections to the correct row owner at shared 3PL locations.
   *
   * BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved Product (not a sku
   * string) so cross-org targets work end-to-end. The modal already has
   * the resolved product (via `useTargetCatalog`).
   */
  onSetStock: (
    product: Product,
    quantity: number,
    locationId?: string,
    targetOrganizationId?: string,
    /** Optional operator reference / order number (Thread Lab stock log). */
    reference?: string,
  ) => Promise<boolean> | boolean;
  products: Product[];
  defaultSku?: string;
}

const IndividualStocktakeModal: React.FC<IndividualStocktakeModalProps> = ({
  isOpen,
  onClose,
  onSetStock,
  products,
  defaultSku,
}) => {
  // `allLocations`: a stocktake COUNTS what is physically there, and stock
  // genuinely sits at delegated locations — those are exactly the rows nobody
  // else will correct.
  const { selectedLocation, allLocations } = useLocation();
  const { organization } = useOrganization();
  const { t } = useTranslation('inventory');
  const [selectedSku, setSelectedSku] = useState("");
  const [quantity, setQuantity] = useState(0);
  // Optional operator reference / order number for the stock log.
  const [reference, setReference] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [targetLocationId, setTargetLocationId] = useState<string>("");
  // BLU-3xx Phase 47.01.2-B-04: cross-org target. Required at shared
  // locations, hidden elsewhere. No default — fresh-pick each open.
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);
  // BLU-3xx Phase 47.01.2-B-fix3.3: in-flight indicator (mirror AddStockModal).
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveLocationId = selectedLocation?.id ?? targetLocationId;

  useEffect(() => {
    if (!isOpen) return;
    // When All Locations is selected, keep the last chosen location for convenience.
    setTargetLocationId((prev) => selectedLocation?.id ?? prev);
  }, [isOpen, selectedLocation?.id]);

  // Reset target org when modal opens or location changes.
  useEffect(() => {
    if (!isOpen) return;
    setTargetOrgId(null);
  }, [isOpen, effectiveLocationId]);

  // BLU-3xx Phase 47.01.2-B-fix3: clear stale SKU when target org changes.
  useEffect(() => {
    setSelectedSku("");
    setSearchQuery("");
  }, [targetOrgId]);

  // BLU-3xx Phase 47.01.2-B-fix3: focus-jump from picker → SKU trigger
  // when the operator picks a client. One keystroke instead of one click.
  const skuTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (!targetOrgId) return;
    const id = window.setTimeout(() => {
      skuTriggerRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [isOpen, targetOrgId]);

  const locationProductsQuery = trpc.inventory.getProducts.useQuery(
    effectiveLocationId ? { locationId: effectiveLocationId } : {},
    {
      enabled: isOpen && !selectedLocation && !!effectiveLocationId,
      staleTime: 30_000,
    }
  );

  const ownProducts: Product[] = useMemo(() => {
    if (selectedLocation) return products;
    const data = locationProductsQuery.data ?? [];
    return data.map((item) => ({
      id: item.id,
      sku: item.sku,
      title: item.title ?? undefined,
      category: {
        type: item.category?.type ?? undefined,
        size: item.category?.size ?? undefined,
        color: item.category?.color ?? undefined,
      },
      stockLevel: item.stockLevel,
      onHand: item.onHand ?? undefined,
      available: item.available ?? undefined,
      committed: item.committed ?? undefined,
      unavailable: item.unavailable ?? undefined,
      incoming: item.incoming ?? undefined,
      image_url: item.imageUrl ?? undefined,
      notify: item.notify ?? undefined,
      updated_at: item.updatedAt ?? undefined,
      skuCostOverride: item.skuCostOverride ?? null,
      effectiveCost: item.effectiveCost ?? undefined,
      isLocked: item.isLocked ?? undefined,
      productionType: ((item.productionType ?? 'POD') as 'PAP' | 'POD'),
      // BLU-669: MUST be carried through — filterToTargetOrg treats a missing
      // stamp as own-org (legacy fallback), so dropping it here would silently
      // re-admit connected clients' rows under an own-org target in the
      // All-Locations path.
      ownerOrganizationId: item.ownerOrganizationId ?? undefined,
      // BLU-1268: carried so a Just in time pair stays out of the list below.
      stockMode: item.stockMode ?? null,
    }));
  }, [selectedLocation, products, locationProductsQuery.data]);

  // BLU-3xx Phase 47.01.2-B-fix3: cross-org SKU search via shared hook.
  const {
    options: selectableProducts,
    isLoading: isLoadingTargetCatalog,
    isEmpty: isTargetCatalogEmpty,
    isCrossOrg,
  } = useTargetCatalog({
    ownOrgId: organization?.id,
    targetOrgId,
    locationId: effectiveLocationId,
    ownProducts,
    isOpen,
  });

  // BLU-3xx Phase 47.01.2-B-04 + B-fix MED-6: shared cache via
  // `useConnectedClients` — React Query dedupes against
  // `useClientOrgFilter` and `ClientTargetPicker`.
  const { clients: connectedClients } = useConnectedClients({
    locationId: effectiveLocationId,
    enabled:
      isOpen && SHARED_LOCATIONS_ENABLED && !!organization?.id,
  });

  const showPicker =
    SHARED_LOCATIONS_ENABLED &&
    !!organization?.id &&
    !!effectiveLocationId &&
    connectedClients.length > 0;

  const isOwnOrgTarget = !!targetOrgId && targetOrgId === organization?.id;

  // BLU-669: controlled SKU-select open state + focus-redirect flag for the
  // own-org empty-state CTA — Radix Select traps focus while open and
  // re-focuses its own trigger on close; see AddStockModal for the mechanism.
  const [skuSelectOpen, setSkuSelectOpen] = useState(false);
  const redirectFocusToPickerRef = useRef(false);

  // BLU-669: annotate the own-org picker option when the caller's own
  // (filtered) catalog at this location is empty (see AddStockModal).
  const ownCatalogEmpty = useMemo(
    () =>
      !!organization?.id &&
      !locationProductsQuery.isFetching &&
      filterToTargetOrg(ownProducts, organization.id, organization.id).length === 0,
    [ownProducts, organization?.id, locationProductsQuery.isFetching],
  );

  // BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.4: i18n the (You) suffix
  // template + the "Your org" fallback so zh-CN users see localised
  // strings.
  const resolvedTargetName = useMemo(() => {
    if (!targetOrgId) return null;
    if (targetOrgId === organization?.id) {
      return organization?.name
        ? t('crossOrg.picker.youSuffix', { name: organization.name })
        : t('crossOrg.picker.yourOrgFallback');
    }
    const match = connectedClients.find((c) => c.id === targetOrgId);
    return match?.name ?? null;
  }, [targetOrgId, organization?.id, organization?.name, connectedClients, t]);

  // BLU-1268: a Just in time pair has no shelf count (its stock figure is the
  // store constant, 9999) and the server refuses a count on it, so it is never
  // offered. One preset from a selected row is said out loud instead.
  const countableProducts = useMemo(
    () => selectableProducts.filter((product) => !isJustInTimePair(product)),
    [selectableProducts],
  );
  const justInTimeSelected =
    !!selectedSku &&
    selectableProducts.some((product) => product.sku === selectedSku && isJustInTimePair(product));

  const handleSubmit = async () => {
    if (!selectedSku || quantity < 0 || !effectiveLocationId) return;
    if (showPicker && !targetOrgId) return; // gate enforced
    if (isSubmitting) return;

    // BLU-3xx Phase 47.01.2-B-fix3.1: resolve from `selectableProducts`
    // (cross-org-aware), not `products` prop.
    const selectedProductForSubmit = countableProducts.find(
      (p) => p.sku === selectedSku,
    );
    if (!selectedProductForSubmit) return;

    setIsSubmitting(true);
    try {
      const ok = await onSetStock(
        selectedProductForSubmit,
        quantity,
        effectiveLocationId,
        targetOrgId ?? undefined,
        reference.trim() || undefined,
      );
      if (ok) {
        // BLU-3xx Phase 47.01.2-B-fix3.6 (#1b): modal-level success toast.
        // useInventoryMutations.setStockMutation lost its legacy toast in
        // B-fix3.5 (it rendered "Unknown product" for cross-org SKUs).
        // Owning the toast here lets us label it with the resolved SKU.
        toast.success(t('stocktakeModal.successTitle'), {
          description: t('stocktakeModal.successDescription', {
            sku: selectedProductForSubmit.sku,
            quantity,
          }),
        });
        resetForm();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedSku("");
    setQuantity(0);
    setReference("");
    setSearchQuery("");
    // BLU-3xx Phase 47.01.2-B-fix3.6 (#1a): keep targetOrgId so the
    // operator can immediately stocktake the next SKU for the same client.
    // Picker is fresh-picked on modal open via the effect above.
  };

  const filteredProducts = countableProducts.filter((product) => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return true;
    const haystack = [
      product.sku,
      product.category?.type,
      product.category?.size,
      product.category?.color,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  });

  const selectedProduct = countableProducts.find(p => p.sku === selectedSku);
  const missingSkuAtLocation =
    !selectedLocation &&
    !!effectiveLocationId &&
    !!selectedSku &&
    !selectedProduct &&
    !justInTimeSelected &&
    !locationProductsQuery.isFetching;

  // Preselect a SKU when provided
  useEffect(() => {
    if (isOpen && defaultSku) {
      setSelectedSku(defaultSku);
    }
  }, [isOpen, defaultSku]);

  const submitDisabled =
    !selectedSku ||
    quantity < 0 ||
    !selectedProduct ||
    (!selectedLocation && !effectiveLocationId) ||
    (showPicker && !targetOrgId) ||
    isSubmitting;

  // BLU-3xx Phase 47.01.2-B-04: Cmd/Ctrl+Enter commits when valid; respects
  // the picker gate.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (!submitDisabled) {
        event.preventDefault();
        void handleSubmit();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>{t('stocktakeModal.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('stocktakeModal.a11yDescription')}
          </DialogDescription>
          {targetOrgId && resolvedTargetName && (
            <div className="pt-1">
              <ClientTargetChip
                resolvedName={resolvedTargetName}
                label={t('crossOrg.picker.labelStocktake')}
                isOwnOrg={isOwnOrgTarget}
              />
            </div>
          )}
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {!selectedLocation && (
            <div className="grid gap-2">
              <Label>{t('addStockModal.locationLabel')}</Label>
              <Select value={targetLocationId} onValueChange={setTargetLocationId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('addStockModal.selectLocation')} />
                </SelectTrigger>
                <SelectContent>
                  <StockWriteLocationItems locations={allLocations} />
                </SelectContent>
              </Select>
            </div>
          )}
          <Alert className="border-orange-200 bg-orange-50">
            <ClipboardCheck className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>{t('stocktakeModal.noteLabel')}</strong> {t('stocktakeModal.note')}
            </AlertDescription>
          </Alert>

          {/* BLU-3xx Phase 47.01.2-B-04 + B-fix3: cross-org target picker.
              Reordered above SKU — picker is the first decision; SKU
              dropdown gates on it. Subtle bordered group signals "this
              is the gating decision". */}
          {showPicker && organization && (
            <div className="rounded-md border border-border/60 bg-muted/20 p-3">
              <ClientTargetPicker
                value={targetOrgId}
                onChange={setTargetOrgId}
                ownOrgId={organization.id}
                ownOrgName={organization.name ?? t('crossOrg.picker.yourOrgFallback')}
                locationId={effectiveLocationId ?? ''}
                required
                label={t('crossOrg.picker.labelStocktake')}
                id="stocktake-target-picker"
                ownOrgSubtext={
                  ownCatalogEmpty ? t('crossOrg.picker.ownOrgNoCatalog') : undefined
                }
              />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="sku">{t('addStockModal.skuLabel')}</Label>
            <Select
              value={selectedSku}
              onValueChange={setSelectedSku}
              open={skuSelectOpen}
              onOpenChange={setSkuSelectOpen}
            >
              <SelectTrigger
                ref={skuTriggerRef}
                disabled={
                  (!selectedLocation && !effectiveLocationId) ||
                  (showPicker && !targetOrgId)
                }
                className="transition-colors duration-200"
              >
                {showPicker && !targetOrgId ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Lock className="h-4 w-4" />
                    {t('crossOrg.modal.skuPickClientFirst')}
                  </span>
                ) : (
                  <SelectValue placeholder={t('addStockModal.selectProduct')} />
                )}
              </SelectTrigger>
              <SelectContent
                onCloseAutoFocus={(event) => {
                  // BLU-669: CTA-initiated close — land on the target picker
                  // instead of Radix's default focus-return-to-trigger.
                  if (redirectFocusToPickerRef.current) {
                    redirectFocusToPickerRef.current = false;
                    event.preventDefault();
                    document.getElementById('stocktake-target-picker')?.focus();
                  }
                }}
              >
                <div className="p-2">
                  <Input
                    placeholder={t('addStockModal.searchProducts')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mb-2"
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                </div>
                {isLoadingTargetCatalog ? (
                  <>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={`sk-${i}`} className="mx-2 mb-1 h-9" />
                    ))}
                  </>
                ) : isTargetCatalogEmpty && isOwnOrgTarget && !locationProductsQuery.isFetching ? (
                  // BLU-669: own-org target with no own catalog at this
                  // location (see AddStockModal — same rationale). CTA lands
                  // focus on the target picker; a client is the recovery.
                  <div className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyOwnCatalog', {
                        name: organization?.name ?? t('crossOrg.picker.yourOrgFallback'),
                      })}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyOwnCatalogHelp')}
                    </p>
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        // Radix Select traps focus while open — arm the
                        // redirect and close; onCloseAutoFocus lands it.
                        redirectFocusToPickerRef.current = true;
                        setSkuSelectOpen(false);
                      }}
                    >
                      {t('crossOrg.modal.skuEmptyOwnCatalogCta')}
                    </Button>
                  </div>
                ) : isCrossOrg && isTargetCatalogEmpty && resolvedTargetName ? (
                  <div className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyCatalog', {
                        client: resolvedTargetName,
                      })}
                    </p>
                    {/* BLU-669: /products?targetOrg CTA removed — dead end
                        that would create an OWN-org SKU (see AddStockModal). */}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('crossOrg.modal.skuEmptyCatalogHelp', {
                        client: resolvedTargetName,
                      })}
                    </p>
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <SelectItem key={product.sku} value={product.sku} className="group">
                      <div className="flex flex-col">
                        <span className="group-data-[state=checked]:text-accent-foreground group-data-[highlighted]:text-accent-foreground">
                          {product.sku}
                        </span>
                        <span className="text-xs text-muted-foreground group-data-[state=checked]:text-accent-foreground group-data-[highlighted]:text-accent-foreground">
                          {product.category.type || ""}
                          {product.category.size ? ` - ${product.category.size}` : ""}
                          {" • "}
                          {t('stocktakeModal.currentLabel')} {product.stockLevel}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {!selectedLocation && !effectiveLocationId && (
              <p className="text-xs text-muted-foreground">{t('addStockModal.chooseLocationFirst')}</p>
            )}
            {!selectedLocation && effectiveLocationId && locationProductsQuery.isFetching && (
              <p className="text-xs text-muted-foreground">{t('addStockModal.loadingProducts')}</p>
            )}
            {/* Helper paragraph dropped (post-review): in-trigger label
                + Lock icon already convey the gate. */}
          </div>

          {selectedProduct && (
            <div className="p-3 bg-muted/30 rounded-lg">
              <div className="text-sm">
                <strong>{t('stocktakeModal.currentStockLevel')}</strong> {selectedProduct.stockLevel}
              </div>
            </div>
          )}
          {justInTimeSelected && (
            <WarningNotice role="status" data-testid="stocktake-just-in-time">
              {t('supply.stockRefused')}
            </WarningNotice>
          )}
          {missingSkuAtLocation && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertDescription className="text-amber-800">
                {t('stocktakeModal.notStockedAtLocation')}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-2">
            <Label htmlFor="quantity">{t('stocktakeModal.newStockLevel')}</Label>
            <Input
              id="quantity"
              type="number"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              placeholder={t('stocktakeModal.enterExactCount')}
            />
          </div>

          <AdjustmentReferenceField
            id="stocktake-reference"
            value={reference}
            onChange={setReference}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('stocktakeModal.cancel')}
          </Button>
          {/* BLU-3xx Phase 47.01.2-B-fix MED-9: brand-purple matches
              the rest of the cross-org write modals. Per DESIGN.md
              §3.4, emerald is reserved for "Mark Paid (constructive
              lock)" — stocktake isn't a lock and shouldn't borrow
              that affordance. */}
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90 disabled:opacity-50"
            aria-keyshortcuts="Meta+Enter"
            aria-busy={isSubmitting || undefined}
          >
            {isSubmitting && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            )}
            {t('stocktakeModal.setStockLevel')}
            {/* BLU-3xx Phase 47.01.2-B-fix MED-8: Cmd/Ctrl+Enter hint. */}
            {!isSubmitting && (
              <kbd className="ml-1 rounded border border-border/60 bg-white/10 px-1 text-[10px] font-mono">
                ⌘↵
              </kbd>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IndividualStocktakeModal;
